// src/templateManager/components/tabs/ImpactCategoriesTab.jsx
import React from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import {
  Stack,
  Card,
  Text,
  Button,
  Group,
  Alert,
  TextInput,
  Textarea,
  Badge,
  ActionIcon,
} from '@mantine/core';
import {
  IconPlus,
  IconInfoCircle,
  IconTrash,
  IconGripVertical,
  IconChartBar,
  IconArrowUp,
  IconArrowDown
} from '@tabler/icons-react';

/**
 * ImpactCategoriesTab - Configure impact categories for risk evaluation
 * Impact categories define the dimensions used to evaluate risk consequences
 * (formerly "risk themes")
 */
function ImpactCategoriesTab() {
  const { control, watch, setValue } = useFormContext();
  const categories = watch('taxonomy.impactCategories') || [];

  /**
   * Add new impact category
   */
  const addCategory = () => {
    const newCategory = {
      id: Date.now(),
      name: 'New Impact Category',
      description: '',
      order: categories.length + 1
    };
    setValue('taxonomy.impactCategories', [...categories, newCategory], { shouldDirty: true });
  };

  /**
   * Remove impact category and reorder remaining
   */
  const removeCategory = (index) => {
    const updated = categories.filter((_, i) => i !== index);
    // Reorder remaining categories
    updated.forEach((cat, i) => {
      cat.order = i + 1;
    });
    setValue('taxonomy.impactCategories', updated, { shouldDirty: true });
  };

  /**
   * Move category up or down in order
   */
  const moveCategory = (index, direction) => {
    const updated = [...categories];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;

    // Swap categories
    [updated[index], updated[targetIndex]] = [updated[targetIndex], updated[index]];

    // Update order fields
    updated.forEach((cat, i) => {
      cat.order = i + 1;
    });

    setValue('taxonomy.impactCategories', updated, { shouldDirty: true });
  };

  /**
   * Get emoji icon based on category name
   */
  const getCategoryEmoji = (name) => {
    const lowerName = (name || '').toLowerCase();

    if (lowerName.includes('financial') || lowerName.includes('economic')) return '💰';
    if (lowerName.includes('health') || lowerName.includes('wellbeing') || lowerName.includes('well-being')) return '🏥';
    if (lowerName.includes('environment') || lowerName.includes('climate') || lowerName.includes('sustainability')) return '🌍';
    if (lowerName.includes('rights') || lowerName.includes('legal') || lowerName.includes('fundamental')) return '⚖️';
    if (lowerName.includes('service') || lowerName.includes('public') || lowerName.includes('access')) return '🏛️';
    if (lowerName.includes('safety') || lowerName.includes('security')) return '🛡️';
    if (lowerName.includes('social') || lowerName.includes('societal')) return '👥';
    if (lowerName.includes('education') || lowerName.includes('learning')) return '📚';
    if (lowerName.includes('data') || lowerName.includes('privacy')) return '🔒';
    if (lowerName.includes('reputation') || lowerName.includes('trust')) return '⭐';

    return '📊';
  };

  return (
    <Stack gap="lg">
      {/* Info Alert */}
      <Alert icon={<IconInfoCircle size={16} />} color="blue">
        Impact categories define the dimensions used to evaluate risk consequences.
        These replace the legacy "risk themes" system and are specific to this template.
      </Alert>

      {/* Empty State */}
      {categories.length === 0 ? (
        <Card withBorder p="xl">
          <Stack align="center" gap="md">
            <IconChartBar size={48} stroke={1.5} color="var(--mantine-color-gray-5)" />
            <Text size="lg" c="dimmed">No impact categories configured</Text>
            <Text size="sm" c="dimmed" ta="center" maw={400}>
              Impact categories are used during risk evaluation to assess consequences
              across different dimensions like Financial, Health, Environment, etc.
            </Text>
            <Button leftSection={<IconPlus size={16} />} onClick={addCategory}>
              Add Impact Category
            </Button>
          </Stack>
        </Card>
      ) : (
        <>
          {/* Categories List */}
          <Stack gap="sm">
            {categories.map((category, index) => (
              <Card key={category.id} withBorder p="md">
                <Group align="flex-start" wrap="nowrap" gap="md">
                  {/* Drag Handle (visual indicator) */}
                  <IconGripVertical
                    size={20}
                    color="var(--mantine-color-gray-5)"
                    style={{ cursor: 'grab', marginTop: '8px' }}
                  />

                  {/* Emoji Icon */}
                  <Text size="xl" style={{ marginTop: '4px' }}>
                    {getCategoryEmoji(category.name)}
                  </Text>

                  {/* Category Details */}
                  <Stack gap="xs" style={{ flex: 1 }}>
                    <Controller
                      name={`taxonomy.impactCategories.${index}.name`}
                      control={control}
                      render={({ field }) => (
                        <TextInput
                          {...field}
                          placeholder="Category name"
                          variant="filled"
                          size="sm"
                          styles={{ input: { fontWeight: 600 } }}
                        />
                      )}
                    />

                    <Controller
                      name={`taxonomy.impactCategories.${index}.description`}
                      control={control}
                      render={({ field }) => (
                        <Textarea
                          {...field}
                          placeholder="Describe this impact category..."
                          variant="filled"
                          size="xs"
                          minRows={2}
                          maxRows={4}
                        />
                      )}
                    />
                  </Stack>

                  {/* Order Badge */}
                  <Badge size="lg" variant="filled" color="blue" style={{ marginTop: '8px' }}>
                    #{category.order}
                  </Badge>

                  {/* Actions */}
                  <Group gap="xs" style={{ marginTop: '8px' }}>
                    <ActionIcon
                      variant="subtle"
                      size="md"
                      onClick={() => moveCategory(index, 'up')}
                      disabled={index === 0}
                      title="Move up"
                    >
                      <IconArrowUp size={16} />
                    </ActionIcon>
                    <ActionIcon
                      variant="subtle"
                      size="md"
                      onClick={() => moveCategory(index, 'down')}
                      disabled={index === categories.length - 1}
                      title="Move down"
                    >
                      <IconArrowDown size={16} />
                    </ActionIcon>
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      size="md"
                      onClick={() => removeCategory(index)}
                      title="Delete category"
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Group>
                </Group>
              </Card>
            ))}
          </Stack>

          {/* Add Button */}
          <Button
            variant="light"
            leftSection={<IconPlus size={16} />}
            onClick={addCategory}
            fullWidth
          >
            Add Impact Category
          </Button>
        </>
      )}

      {/* Summary */}
      <Card withBorder p="md" bg="gray.0">
        <Stack gap="xs">
          <Text size="sm" fw={600}>Summary</Text>
          <Group gap="md">
            <Badge variant="dot">Total Categories: {categories.length}</Badge>
            <Badge variant="dot" color="blue">
              Used in: Risk Evaluation phase
            </Badge>
          </Group>
          <Text size="xs" c="dimmed">
            These categories will be used when evaluating the impact of identified risks
          </Text>
        </Stack>
      </Card>
    </Stack>
  );
}

export default ImpactCategoriesTab;
